import { useMemo, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { AlertCircle, FileQuestion, Layers3, Loader2, RefreshCw } from 'lucide-react'
import type { ContentHierarchyNode } from '@/api/admin'
import type { QuizGroupAssociation } from '@/types/quizSet'
import { buildAiqModuleInspection } from './aiqModuleInspectionModel'
import { QuizSequenceInspector } from './QuizSequenceInspector'

interface AiqModuleInspectionProps {
  module: ContentHierarchyNode
  associations: QuizGroupAssociation[]
  isLoading: boolean
  error: Error | null
  onRetry: () => void
}

export function AiqModuleInspection({
  module,
  associations,
  isLoading,
  error,
  onRetry,
}: AiqModuleInspectionProps) {
  const model = useMemo(
    () => buildAiqModuleInspection(module, associations),
    [associations, module],
  )
  const allGroups = model.lessons.flatMap((lesson) => lesson.groups)
  const defaultGroup = allGroups.find((group) => group.quizIds.length > 0) ?? allGroups[0]
  const [selectedAssociationId, setSelectedAssociationId] = useState<number | null>(null)
  const selectedGroup =
    allGroups.find((group) => group.associationId === selectedAssociationId) ?? defaultGroup
  const selectedLesson = selectedGroup
    ? model.lessons.find((lesson) => lesson.id === selectedGroup.lessonId)
    : undefined

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" />
        AIQ 문제 그룹을 불러오는 중입니다
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-rose-400" />
          <p className="mt-3 text-sm font-medium text-slate-700">AIQ 문제 그룹을 불러오지 못했습니다</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
          >
            <RefreshCw className="h-4 w-4" />
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="grid min-h-full grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)]">
      <aside className="border-b border-slate-200 bg-white lg:border-b-0 lg:border-r">
        <div className="border-b border-slate-200 px-4 py-4">
          <div className="flex items-start gap-2.5">
            <Layers3 className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="min-w-0">
              <h2 className="break-words text-sm font-semibold text-slate-900"><ContentMetadataText text={model.moduleTitle} /></h2>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs tabular-nums text-slate-500">
                <span>레슨 {model.summary.lessonCount}</span>
                <span>그룹 {model.summary.groupCount}</span>
                <span>문제 {model.summary.quizCount}</span>
              </div>
            </div>
          </div>
        </div>

        {model.lessons.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-slate-400">
            이 Module에는 레슨이 없습니다
          </p>
        ) : (
          <div className="divide-y divide-slate-200">
            {model.lessons.map((lesson) => (
              <section key={lesson.id} className="py-3">
                <div className="flex items-start justify-between gap-3 px-4">
                  <h3 className="min-w-0 break-words text-sm font-medium text-slate-800">
                    <ContentMetadataText text={lesson.title} />
                  </h3>
                  <span className="shrink-0 text-xs text-slate-400">{lesson.groups.length}그룹</span>
                </div>
                {lesson.groups.length === 0 ? (
                  <p className="px-4 pb-1 pt-2 text-xs font-medium text-rose-600">
                    연결된 AIQ 문제 그룹 없음
                  </p>
                ) : (
                  <div className="mt-2 space-y-1 px-2">
                    {lesson.groups.map((group) => {
                      const isSelected = selectedGroup?.associationId === group.associationId
                      return (
                        <button
                          key={group.associationId}
                          type="button"
                          onClick={() => setSelectedAssociationId(group.associationId)}
                          aria-pressed={isSelected}
                          className={`flex w-full items-center gap-2 border-l-2 px-3 py-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500 ${
                            isSelected
                              ? 'border-amber-500 bg-amber-50 text-amber-900'
                              : 'border-transparent text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <FileQuestion className={`h-4 w-4 shrink-0 ${isSelected ? 'text-amber-600' : 'text-slate-400'}`} />
                          <span className="min-w-0 flex-1 break-words text-xs font-medium">{group.name}</span>
                          <span className="shrink-0 text-xs tabular-nums text-slate-400">
                            {group.quizIds.length}문제
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </aside>

      <section className="min-w-0 bg-slate-50 p-4 sm:p-6">
        {!selectedGroup ? (
          <div className="flex min-h-72 items-center justify-center text-center">
            <div>
              <FileQuestion className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">검수할 AIQ 문제 그룹이 없습니다</p>
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-5xl space-y-4">
            <header className="border-b border-slate-200 pb-3">
              <p className="text-xs text-slate-500"><ContentMetadataText text={model.moduleTitle} /> / <ContentMetadataText text={selectedLesson?.title} /></p>
              <h2 className="mt-1 break-words text-base font-semibold text-slate-900">
                {selectedGroup.name}
              </h2>
            </header>
            <QuizSequenceInspector
              quizIds={selectedGroup.quizIds}
              sequenceKey={selectedGroup.associationId}
            />
          </div>
        )}
      </section>
    </div>
  )
}
